import { useMemo } from "react";

import type {
    MarketResearchDemandAnalysisCalculationModelsNodeTrendData,
    MarketResearchDemandAnalysisSearchTrendsListData,
    MarketResearchDemandAnalysisSearchTrendsListParams,
} from "@similarweb/pro-api-client";

import { buildKeywordGroupKeywordListId } from "app/modules/demand-analysis/keyword-lists";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { useApiQuery } from "app/modules/api-client/useApiQuery";
import DurationService, { BasicDurations } from "app/services/DurationService";

export type TrendData = MarketResearchDemandAnalysisCalculationModelsNodeTrendData;
export type TrendDataMap = Map<string, TrendData[]>;

const selectTrendDataMap = (data: MarketResearchDemandAnalysisSearchTrendsListData): TrendDataMap => {
    const trendDataMap: TrendDataMap = new Map();

    data.children?.forEach((child) => {
        if (!("identifier" in child)) {
            return;
        }

        const { identifier } = child;
        if (identifier.type === "KeywordGroup" && "keywordGroupId" in identifier) {
            trendDataMap.set(identifier.keywordGroupId, child.trend);
        }
    });

    return trendDataMap;
};

export const useKeywordListsTrendsData = (keywordGroups: IUserKeywordGroup[], country: number) => {
    const { from, to, isDaily, isWindow } = DurationService.getDurationData(
        BasicDurations.LAST_TWENTY_EIGHT_DAYS,
    ).forAPI;

    const queryParams = useMemo(() => {
        return {
            from,
            to,
            isDaily,
            isWindow,
            country,
            keywordListIds: keywordGroups.map((group) => buildKeywordGroupKeywordListId(group.Id)),
        } satisfies MarketResearchDemandAnalysisSearchTrendsListParams;
    }, [from, to, isDaily, isWindow, country, keywordGroups]);

    return useApiQuery(
        {
            tag: "DemandSearchTrends",
            methodName: "marketResearchDemandAnalysisSearchTrendsList",
            options: { enabled: keywordGroups.length > 0, select: selectTrendDataMap },
        },
        queryParams,
    );
};
